import PageHeader from "../../components/Header/PageHeader";
import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Collapse, Select} from "antd";
import axios from "axios";
import AppNavigation from "../../components/AppNavigation";
import {ImeanHeader} from "../../components/Header/Header";
import {StickyNavigationHeader} from "../../components/Navigation/StickyNavigationHeader";

const defaultVideos = [
    { id: 1, name_en: "IMEAN.IO", name_ru: "IMEAN.IO", link: "CUxT_a3lmkM" }
];

// выбирает поле по текущему языку: title_{lang} -> title_en -> первая непустая версия
const pickLang = (obj = {}, baseKey, lang, fallback = "en") => {
    if (!obj) return "";
    const direct = obj[`${baseKey}_${lang}`];
    if (typeof direct === "string" && direct.trim()) return direct;

    const fb = obj[`${baseKey}_${fallback}`];
    if (typeof fb === "string" && fb.trim()) return fb;

    // если структура другая — попробуем найти любое *_xx
    const anyKey = Object.keys(obj).find(k => k.startsWith(`${baseKey}_`) && typeof obj[k] === "string" && obj[k].trim());
    return anyKey ? obj[anyKey] : "";
};

const Guideline = () => {
    const [activeVideo, setActiveVideo] = useState(0);
    const [videosList, setVideosList] = useState(defaultVideos);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [faq, setFaq] = useState([]);

    // дергаем данные; перезагружаем при смене языка, чтобы нормализовать подписи
    useEffect(() => {
        axios.get("/data/guideline-faq.json").then((res) => setFaq(res.data || [])).catch(() => setFaq([]));
        axios.get("/data/guideline-videos.json").then((res) => setVideosList(res.data || defaultVideos)).catch(() => setVideosList(defaultVideos));
    }, [i18n.language]);

    const lang = useMemo(() => (i18n.language || "en").split("-")[0], [i18n.language]);

    const handleReplaceVideo = (action) => {
        setActiveVideo((p) => {
            if (action === "increment" && p < (videosList.length - 1)) return p + 1;
            if (action === "decrement" && p > 0) return p - 1;
            return p;
        });
    };

    return (
        <div id="page-top" className="min-h-screen bg-gradient-to-b from-white to-violet-50 pb-16">
            {/* Header */}
            <ImeanHeader initialDark={true} />
            <StickyNavigationHeader headerTitle={t("home.guideline")} onBackClick={() => navigate("/home")} />

            {/* Main */}
            <div className="max-w-7xl mx-auto w-full px-4 md:px-6 lg:px-8 mt-4">
                <div className="grid grid-cols-12 gap-6">
                    {/* LEFT: sticky nav */}
                    <aside className="col-span-12 md:col-span-3 lg:col-span-3">
                        <div className="sticky top-20">
                            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-3">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                    {t("guideline.onThisPage", "On this page")}
                                </div>

                                {/* Videos section */}
                                <div className="mb-4">
                                    <div className="text-[13px] font-medium text-gray-700 mb-1">
                                        {t("guideline.videos", "Videos")}
                                    </div>
                                    <ul className="space-y-1">
                                        {(videosList || []).map((v, i) => (
                                            <li key={i}>
                                                <a
                                                    href={`#video-${i}`}
                                                    className={`block text-sm rounded-md px-2 py-1 transition hover:bg-violet-50 ${
                                                        i === +activeVideo ? "bg-violet-100 text-violet-700" : "text-gray-700"
                                                    }`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setActiveVideo(i);
                                                        const el = document.getElementById(`video-${i}`);
                                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                                    }}
                                                    title={pickLang(v, "name", lang) || `Video #${i + 1}`}
                                                >
                                                    {pickLang(v, "name", lang) || `Video #${i + 1}`}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* FAQ section */}
                                <div>
                                    <div className="text-[13px] font-medium text-gray-700 mb-1">
                                        {t("guideline.faq", "FAQ")}
                                    </div>
                                    <ul className="space-y-1">
                                        {(faq || []).map((item) => (
                                            <li key={item.id}>
                                                <a
                                                    href={`#faq-${item.id}`}
                                                    className="block text-sm rounded-md px-2 py-1 text-gray-700 hover:bg-violet-50 transition"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        const el = document.getElementById(`faq-${item.id}`);
                                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                                    }}
                                                    title={pickLang(item, "title", lang) || "Untitled"}
                                                >
                                                    {pickLang(item, "title", lang) || "Untitled"}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Back to top */}
                            <div className="text-center mt-3">
                                <a
                                    href="#page-top"
                                    className="inline-block text-sm text-gray-600 hover:text-violet-700"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                >
                                    {t("guideline.backToTop", "Back to top ↑")}
                                </a>
                            </div>
                        </div>
                    </aside>

                    {/* RIGHT: content */}
                    <main className="col-span-12 md:col-span-9 lg:col-span-9 space-y-6">
                        {/* Video guide card */}
                        <section id={`video-${activeVideo}`} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                                <h2 className="text-base md:text-lg font-semibold">
                                    {t("guideline.videoGuide", "Video Guide")}
                                </h2>
                                <div className="flex items-center gap-2">
                                    <button
                                        disabled={+activeVideo <= 0}
                                        className={`w-8 h-8 grid place-items-center rounded-lg border transition ${
                                            +activeVideo <= 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white hover:bg-violet-50 text-gray-700"
                                        }`}
                                        onClick={() => handleReplaceVideo("decrement")}
                                        aria-label={t("guideline.prevVideo", "Previous video")}
                                    >
                                        &#60;
                                    </button>

                                    <Select
                                        value={activeVideo}
                                        className="h-full min-w-[220px] md:min-w-[320px]"
                                        onChange={(value) => setActiveVideo(+value)}
                                    >
                                        {(videosList || []).map((item, index) => (
                                            <Select.Option key={index} value={index}>
                                                {pickLang(item, "name", lang) || `Video #${index + 1}`}
                                            </Select.Option>
                                        ))}
                                    </Select>

                                    <button
                                        disabled={+activeVideo >= (videosList?.length || 1) - 1}
                                        className={`w-8 h-8 grid place-items-center rounded-lg border transition ${
                                            +activeVideo >= (videosList?.length || 1) - 1
                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                : "bg-white hover:bg-violet-50 text-gray-700"
                                        }`}
                                        onClick={() => handleReplaceVideo("increment")}
                                        aria-label={t("guideline.nextVideo", "Next video")}
                                    >
                                        &#62;
                                    </button>
                                </div>
                            </div>

                            {/* Video */}
                            <div className="relative">
                                <iframe
                                    id={`video-${activeVideo}`}
                                    className="w-full aspect-video"
                                    src={`https://www.youtube.com/embed/${videosList?.[activeVideo]?.link || ""}?hl=${lang}`}
                                    lang={lang}
                                    title="Guidelines video"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                />
                            </div>

                            {/* Optional description */}
                            {Boolean(pickLang(videosList?.[activeVideo], "desc", lang)) && (
                                <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
                                    {pickLang(videosList?.[activeVideo], "desc", lang)}
                                </div>
                            )}
                        </section>

                        {/* FAQ card */}
                        <section id="faq" className="bg-white border border-gray-100 rounded-2xl shadow-sm p-3 md:p-4">
                            <h2 className="text-base md:text-lg font-semibold mb-3">
                                {t("guideline.faqFull", "Frequently Asked Questions")}
                            </h2>
                            <Collapse accordion defaultActiveKey={[String(faq?.[0]?.id || "0")]} className="bg-white">
                                {(faq || []).map((item) => (
                                    <Collapse.Panel header={pickLang(item, "title", lang) || "Untitled"} key={item.id}>
                                        <div id={`faq-${item.id}`} className="scroll-mt-24" />
                                        {(item?.content || []).map((content) =>
                                                content?.isHtml ? (
                                                    <div
                                                        key={content.id}
                                                        className="mb-4 prose max-w-none"
                                                        dangerouslySetInnerHTML={{ __html: pickLang(content, "text", lang) || "" }}
                                                    />
                                                ) : (
                                                    <div className="mb-4" key={content.id}>
                                                        {pickLang(content, "title", lang) && (
                                                            <h5 className="text-base md:text-lg font-semibold text-violet-600">
                                                                {pickLang(content, "title", lang)}
                                                            </h5>
                                                        )}
                                                        <pre className="block whitespace-pre-wrap break-words text-sm md:text-base">
                            {pickLang(content, "text", lang) || ""}
                          </pre>
                                                    </div>
                                                )
                                        )}
                                    </Collapse.Panel>
                                ))}
                            </Collapse>
                        </section>
                    </main>
                </div>
            </div>

            <AppNavigation />
        </div>
    );
};

export default Guideline;
